import React,{useState,useEffect} from 'react';
import {BrowserRouter as Router,Routes,Route} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import EnhancedWelcome from './components/EnhancedWelcome';
import EnhancedBasicInfo from './components/EnhancedBasicInfo';
import NicheDefinition from './components/NicheDefinition';
import GoalSetting from './components/GoalSetting';
import ActionPlan from './components/ActionPlan';
import MarketingAnalysis from './components/MarketingAnalysis';
import CompletePlan from './components/CompletePlan';
import Navigation from './components/Navigation';
import ProgressBar from './components/ProgressBar';
import './App.css';

function App() {
  const [formData,setFormData]=useState(()=> {
    // Load saved data from localStorage on initial render
    const savedData=localStorage.getItem('marketingPlanData');
    return savedData ? JSON.parse(savedData) : {
      // Basic Info
      name: '',
      subject: '',
      experience: '',
      currentStudents: '',
      teachingMethod: '',
      targetAudience: '',
      location: '',
      currentRevenue: '',
      targetRevenue: '',
      certifications: '',
      marketingChannels: [],
      marketingBudget: '',
      timeCommitment: '',
      // Niche
      niche: '',
      uniqueValue: '',
      targetMarket: '',
      // Goals
      monthlyGoals: [],
      sixMonthGoals: [],
      yearlyGoals: [],
      // Action Plan
      actionPlan: {},
      // Analysis
      marketingAnalysis: {}
    };
  });
  
  const [currentStep,setCurrentStep]=useState(()=> {
    // Load saved step from localStorage
    const savedStep=localStorage.getItem('marketingPlanStep');
    return savedStep ? parseInt(savedStep) : 0;
  });

  // Save to localStorage whenever formData or currentStep changes
  useEffect(()=> {
    localStorage.setItem('marketingPlanData',JSON.stringify(formData));
    localStorage.setItem('marketingPlanStep',currentStep.toString());
  },[formData,currentStep]);

  const steps=[
    {path: '/',component: EnhancedWelcome,title: 'Welcome'},
    {path: '/basic-info',component: EnhancedBasicInfo,title: 'Basic Information'},
    {path: '/niche',component: NicheDefinition,title: 'Define Your Niche'},
    {path: '/goals',component: GoalSetting,title: 'Set S.M.A.R.T Goals'},
    {path: '/action-plan',component: ActionPlan,title: 'Create Action Plan'},
    {path: '/marketing-analysis',component: MarketingAnalysis,title: 'Marketing Analysis'},
    {path: '/complete-plan',component: CompletePlan,title: 'Your Marketing Plan'}
  ];

  const updateFormData=(newData)=> {
    setFormData(prev=> ({...prev,...newData}));
  };

  const clearAllData=()=> {
    localStorage.removeItem('marketingPlanData');
    localStorage.removeItem('marketingPlanStep');
    setFormData({
      name: '',
      subject: '',
      experience: '',
      currentStudents: '',
      teachingMethod: '',
      targetAudience: '',
      location: '',
      currentRevenue: '',
      targetRevenue: '',
      certifications: '',
      marketingChannels: [],
      marketingBudget: '',
      timeCommitment: '',
      niche: '',
      uniqueValue: '',
      targetMarket: '',
      monthlyGoals: [],
      sixMonthGoals: [],
      yearlyGoals: [],
      actionPlan: {},
      marketingAnalysis: {}
    });
    setCurrentStep(0);
    window.location.hash='/';
  };

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <motion.div 
            initial={{opacity: 0,y: -20}}
            animate={{opacity: 1,y: 0}}
            className="text-center mb-8"
          >
            <h1 className="text-4xl font-bold text-gray-800 mb-2">PlanMyGrowth</h1>
            <p className="text-lg text-gray-600">Build your personalized marketing strategy step by step</p>
            
            {/* Auto-save indicator and clear button */}
            <div className="mt-4 flex justify-center items-center space-x-4">
              <div className="flex items-center text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span>Auto-saved</span>
              </div>
              <button 
                onClick={clearAllData}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                Start Fresh
              </button>
            </div>
          </motion.div>
          
          <ProgressBar currentStep={currentStep} totalSteps={steps.length} />
          
          <AnimatePresence mode="wait">
            <Routes>
              {steps.map((step,index)=> (
                <Route 
                  key={step.path} 
                  path={step.path} 
                  element={
                    <motion.div
                      initial={{opacity: 0,x: 20}}
                      animate={{opacity: 1,x: 0}}
                      exit={{opacity: 0,x: -20}}
                      transition={{duration: 0.3}}
                    >
                      <step.component 
                        formData={formData} 
                        updateFormData={updateFormData} 
                        setCurrentStep={setCurrentStep} 
                        currentStep={index} 
                      />
                    </motion.div>
                  } 
                />
              ))}
            </Routes>
          </AnimatePresence>
          
          <Navigation currentStep={currentStep} />
        </div>
        <footer className="bg-gray-800 text-white py-6 mt-16">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm">
              Build with ❤️ by{' '}
              <a 
                href="https://super-teacher.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                SuperTeacher
              </a>
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;